import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { prefersReducedMotion } from '$javascript/utils/utils'

const Hero = () => {
  const heroRef = useRef(null)

  // Parallax effect for background
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  })

  const backgroundY = prefersReducedMotion()
    ? 0
    : useTransform(scrollYProgress, [0, 1], ['0%', '30%'])

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  }

  const itemVariants = prefersReducedMotion()
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.4 } }
      }
    : {
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }
        }
      }

  const imageVariants = prefersReducedMotion()
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.6, delay: 0.5 } }
      }
    : {
        hidden: { opacity: 0, x: 40 },
        visible: {
          opacity: 1,
          x: 0,
          transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.5 }
        }
      }

  return (
    <section
      ref={heroRef}
      className="hero-section"
      style={{ position: 'relative' }}
    >
      {/* Parallax Background Decoration */}
      <motion.div
        style={{
          y: backgroundY,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '20%',
            right: '-10%',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'var(--gradient-blue)',
            opacity: 0.3,
            filter: 'blur(80px)'
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            left: '-5%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'var(--gradient-orange)',
            opacity: 0.4,
            filter: 'blur(60px)'
          }}
        />
      </motion.div>

      {/* Hero Content */}
      <div className="container mt-2 md:mt-0" style={{ position: 'relative', zIndex: 1, paddingLeft: 'var(--space-md)', paddingRight: 'var(--space-md)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.5fr',
            gap: 'var(--space-2xl)',
            alignItems: 'center',
            maxWidth: '100%'
          }}
          className="hero-grid"
        >
          {/* Left Column: Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Eyebrow Text */}
            <motion.p
              variants={itemVariants}
              style={{
                fontSize: 'var(--text-sm)',
                fontWeight: '600',
                color: 'var(--color-primary-600)',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 'var(--space-md)'
              }}
            >
              For Nonprofits & Advocacy Organizations
            </motion.p>

            {/* Main Headline */}
            <motion.h1
              variants={itemVariants}
              className="!text-blue-600 !text-4xl"
              style={{
                marginBottom: 'var(--space-lg)'
              }}
            >
              Authentic Connections,<br/>Genuine Impact.
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={itemVariants}
              style={{
                fontSize: 'var(--text-xl)',
                color: 'var(--color-neutral-600)',
                marginBottom: 'var(--space-2xl)',
                lineHeight: '1.6'
              }}
            >
              Sincere helps nonprofits create meaningful supporter relationships through
              personalized postcard and letter campaigns that people actually read—and remember.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={itemVariants}
              className="flex-col md:flex-row"
              style={{
                display: 'flex',
                gap: 'var(--space-md)',
                width: '100%'
              }}
            >
              <motion.a
                href="#contact"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                style={{
                  flex: 1,
                  display: 'inline-block',
                  padding: '1rem 2rem',
                  fontSize: 'var(--text-lg)',
                  fontWeight: '600',
                  color: 'white',
                  background: 'var(--gradient-cta)',
                  border: '2px solid transparent',
                  borderRadius: 'var(--radius-lg)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  boxShadow: 'var(--shadow-orange)',
                  transition: 'box-shadow 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-xl)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'var(--shadow-orange)'
                }}
              >
                Get Started
              </motion.a>

              <motion.a
                href="#sincere"
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                style={{
                  flex: 1,
                  display: 'inline-block',
                  padding: '1rem 2rem',
                  fontSize: 'var(--text-lg)',
                  fontWeight: '600',
                  color: 'var(--color-primary-600)',
                  background: 'white',
                  border: '2px solid var(--color-primary-600)',
                  borderRadius: 'var(--radius-lg)',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--color-primary-50)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white'
                }}
              >
                Learn More
              </motion.a>
            </motion.div>

            {/* Trust Badge */}
            <motion.p
              variants={itemVariants}
              style={{
                marginTop: 'var(--space-xl)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-neutral-500)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-xs)'
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ color: 'var(--color-accent-500)' }}
              >
                <path
                  d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z"
                  fill="currentColor"
                />
                <path
                  d="M13.7071 7.29289C14.0976 7.68342 14.0976 8.31658 13.7071 8.70711L9.70711 12.7071C9.31658 13.0976 8.68342 13.0976 8.29289 12.7071L6.29289 10.7071C5.90237 10.3166 5.90237 9.68342 6.29289 9.29289C6.68342 8.90237 7.31658 8.90237 7.70711 9.29289L9 10.5858L12.2929 7.29289C12.6834 6.90237 13.3166 6.90237 13.7071 7.29289Z"
                  fill="white"
                />
              </svg>
              Trusted by leading nonprofits • Over 10 million people engaged
            </motion.p>
          </motion.div>

          {/* Right Column: Image */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={imageVariants}
            style={{
              position: 'relative',
              maxWidth: '100%',
              overflow: 'hidden'
            }}
          >
            <img
              src="/images/home-illustration.jpg"
              alt="Sincere handwritten letters and postcards for nonprofits"
              style={{
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 'var(--radius-2xl)',
                boxShadow: 'var(--shadow-xl)',
                display: 'block'
              }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Hero
